import { noop } from 'lodash';
import { useMemo } from 'react';

import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { FolderIcon } from '@/components/icons/FolderIcon';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { FlyoutMenuItem } from '@/components/poppers/MenuPopper/FlyoutMenuItem';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { compareCreatedAtDescending } from '@/modules/documents/documents.utils';
import { COLORS } from '@/styles/tokens/colors';
import { truncateToEllipis } from '@/utils/formatting/strings';
import { getNodes } from '@/utils/graphqlUtils';

import { useHouseholdDocumentsViewerContext } from '../context/HouseholdDocumentsViewer.context';

interface SummaryOption {
  summaryId: string;
  summaryDisplayName: string;
  documentId: string;
  documentName: string;
  isCombined: boolean;
  createdAt: Date;
}

export function SummarySelector() {
  const {
    targetDocuments,
    otherHouseholdDocuments,
    activeSummaryId,
    updateActiveSummaryId,
    updateActiveDocumentId,
  } = useHouseholdDocumentsViewerContext();

  const targetSummaryOptions = useMemo(() => {
    const options: SummaryOption[] = [];
    (targetDocuments ?? []).forEach((doc) => {
      doc.summaries?.forEach((summary) => {
        options.push({
          summaryId: summary.id,
          summaryDisplayName: summary.displayName?.trim() || 'Summary',
          documentId: doc.id,
          documentName: doc.name,
          isCombined: getNodes(summary.sourceDocuments).length > 1,
          createdAt: summary.createdAt,
        });
      });
    });
    return options.sort(compareCreatedAtDescending);
  }, [targetDocuments]);

  const otherSummaryOptions = useMemo(() => {
    const options: SummaryOption[] = [];
    (otherHouseholdDocuments ?? []).forEach((doc) => {
      doc.summaries?.forEach((summary) => {
        options.push({
          summaryId: summary.id,
          summaryDisplayName: summary.displayName?.trim() || 'Summary',
          documentId: doc.id,
          documentName: doc.name,
          isCombined: getNodes(summary.sourceDocuments).length > 1,
          createdAt: summary.createdAt,
        });
      });
    });
    return options.sort(compareCreatedAtDescending);
  }, [otherHouseholdDocuments]);

  const totalSummaries =
    targetSummaryOptions.length + otherSummaryOptions.length;

  // Hide the selector if there are 0 or 1 summaries across all documents
  if (totalSummaries <= 1) {
    return null;
  }

  const activeSummaryOption =
    targetSummaryOptions.find((opt) => opt.summaryId === activeSummaryId) ??
    otherSummaryOptions.find((opt) => opt.summaryId === activeSummaryId);

  const buttonLabel = activeSummaryOption
    ? truncateToEllipis(
        `${activeSummaryOption.documentName} - ${activeSummaryOption.summaryDisplayName}`,
        50
      )
    : 'Select summary';

  const handleSummarySelect = (option: SummaryOption) => {
    // Update the active document if selecting a summary from a different document
    updateActiveDocumentId({
      documentId: option.documentId,
      shouldClearSearchResults: true,
    });
    updateActiveSummaryId(option.summaryId);
  };

  return (
    <ButtonWithPopover
      size="sm"
      variant="secondary"
      popperVariant="menuBelow"
      label={buttonLabel}
    >
      {otherSummaryOptions.length > 0 && (
        <FlyoutMenuItem
          label="Other existing summaries"
          icon={<FolderIcon size={18} sx={{ color: COLORS.TEXT.SECONDARY }} />}
          muiMenuItemProps={{
            divider: true,
          }}
        >
          {otherSummaryOptions.map((option) => (
            <MenuItem
              key={option.summaryId}
              label={option.documentName}
              subtext={option.summaryDisplayName}
              endAdornment={
                option.isCombined ? (
                  <Badge
                    variant={BadgeVariants.SecondaryOutline}
                    display="Combined"
                  />
                ) : undefined
              }
              onClick={() => handleSummarySelect(option)}
              muiMenuItemProps={{
                selected: option.summaryId === activeSummaryId,
              }}
            />
          ))}
        </FlyoutMenuItem>
      )}
      {targetSummaryOptions.map((option) => (
        <MenuItem
          key={option.summaryId}
          label={option.documentName}
          subtext={option.summaryDisplayName}
          endAdornment={
            option.isCombined ? (
              <Badge
                variant={BadgeVariants.SecondaryOutline}
                display="Combined"
              />
            ) : undefined
          }
          onClick={() => handleSummarySelect(option)}
          muiMenuItemProps={{
            selected: option.summaryId === activeSummaryId,
          }}
        />
      ))}
      {totalSummaries === 0 && (
        <MenuItem onClick={noop} label="No summaries" disabled />
      )}
    </ButtonWithPopover>
  );
}
